import { useEffect, useState } from 'react';
import { api, ApiError } from '../api';
import { t, type Key } from '../i18n';
import { closeApp, haptic } from '../telegram';
import { fmt, money } from './fmt';

type Partner = { id: string; name: string; country: string | null; dpa_signed_at: string | null; done: number; tried: boolean; allowed: boolean };
type Form = {
  case: { id: string; number: string; service: string; origin_country: string | null; dest_country: string | null;
          from_place: string | null; to_place: string | null; window_to: string | null; is_demo: boolean };
  partners: Partner[];
  currency: string;
  offer_pending: boolean;
};

const VIS: { key: string; label: Key }[] = [
  { key: 'price', label: 'v_price' },
  { key: 'family_contacts', label: 'v_family_contacts' },
  { key: 'all_documents', label: 'v_all_documents' },
  { key: 'medical', label: 'v_medical' },
];

/** «Передати партнеру»: вся справа / перевезення, що бачить партнер (ціна й контакти родини — приховані типово),
 *  ціна для партнера, час на відповідь 15/30/60 хв. Пропозиція — одному партнеру; без DPA — лише демо-справа. */
export function Handover({ caseId, orgId }: { caseId: string; orgId?: string | null }) {
  const [form, setForm] = useState<Form | null>(null);
  const [scope, setScope] = useState<'transport' | 'case'>('transport');
  const [partner, setPartner] = useState<string | null>(orgId ?? null);
  const [vis, setVis] = useState<Record<string, boolean>>({ price: false, family_contacts: false, all_documents: false, medical: false });
  const [price, setPrice] = useState('');
  const [currency, setCurrency] = useState('EUR');
  const [wait, setWait] = useState<15 | 30 | 60>(15);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api<Form>(`/tg/app/dispatch/handover-form?case_id=${caseId}`)
      .then((f) => {
        setForm(f);
        setCurrency(f.currency === 'UAH' ? 'EUR' : f.currency);
        if (!orgId) setPartner(f.partners.find((p) => p.allowed && !p.tried)?.id ?? null);
      })
      .catch(() => setMsg(t('error')));
  }, [caseId, orgId]);

  if (!form) return <div className="center">{msg ?? t('loading')}</div>;
  const c = form.case;
  const chosen = form.partners.find((p) => p.id === partner);

  async function send() {
    if (!partner) return;
    setBusy(true);
    try {
      const minor = Math.round(parseFloat(price.replace(',', '.').replace(/\s/g, '') || '0') * 100);
      await api('/tg/app/dispatch/offers', { body: {
        case_id: caseId, to_org_id: partner, scope, wait_min: wait, partner_payment_minor: minor, currency,
        visibility: { route: true, dates: true, transport_documents: true, partner_payment: true, ...vis },
      } });
      haptic('success');
      setMsg(t('sent'));
      setTimeout(closeApp, 1200);
    } catch (e) {
      haptic('error');
      setMsg(e instanceof ApiError ? String(e.detail ?? e.code) : t('error'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="app">
      <div className="body">
        <div>
          <div className="k">{c.number}</div>
          <h1 className="h1">{t('handover_title')}</h1>
          <div className="k">
            {[c.from_place, c.origin_country].filter(Boolean).join(' ')} → {[c.to_place, c.dest_country].filter(Boolean).join(' ')}
            {c.window_to ? ` · ${fmt(c.window_to)}` : ''}
          </div>
        </div>
        {form.offer_pending && <div className="lock">{t('offer_pending')}</div>}

        <div className="card">
          <div className="lbl">{t('what')}</div>
          <div className="seg">
            <button type="button" className={scope === 'case' ? 'on' : ''} onClick={() => setScope('case')}>{t('scope_case')}</button>
            <button type="button" className={scope === 'transport' ? 'on' : ''} onClick={() => setScope('transport')}>{t('scope_transport')}</button>
          </div>
          <div className="k">{t(scope === 'case' ? 'scope_case_note' : 'scope_transport_note')}</div>
        </div>

        <div className="card">
          <div className="lbl">{t('partner')}</div>
          {form.partners.map((p) => (
            <button key={p.id} type="button" className={`opt ${partner === p.id ? 'on' : ''}`} disabled={!p.allowed}
                    onClick={() => setPartner(p.id)}>
              <span className={`dot ${partner === p.id ? 'on' : ''}`} />
              <span style={{ flex: 1 }}>
                <b>{p.name}</b>{p.country ? ` · ${p.country}` : ''}
                <br />
                <span className="k">
                  {p.allowed
                    ? `${p.done} ${t('done_with_us')}${p.dpa_signed_at ? ` · ${t('dpa_signed')} ${fmt(p.dpa_signed_at, true)}` : ''}`
                    : t('dpa_missing')}
                </span>
              </span>
            </button>
          ))}
        </div>

        <div className="card">
          <div className="lbl">{t('sees')}</div>
          {VIS.map((v) => (
            <div className="tgl" key={v.key}>
              <span>{t(v.label)}</span>
              <button type="button" aria-label={t(v.label)} aria-pressed={vis[v.key]} className={`sw ${vis[v.key] ? 'on' : ''}`}
                      onClick={() => setVis({ ...vis, [v.key]: !vis[v.key] })} />
            </div>
          ))}
        </div>

        <div className="card">
          <div className="lbl">{t('partner_price')}</div>
          <label className="price">
            <input inputMode="decimal" placeholder="0" value={price} aria-label={t('partner_price')}
                   onChange={(e) => setPrice(e.target.value)} />
            <select value={currency} onChange={(e) => setCurrency(e.target.value)} aria-label="currency">
              {['EUR', 'UAH', 'MDL', 'PLN', 'USD', 'RON'].map((x) => <option key={x}>{x}</option>)}
            </select>
          </label>
          <div className="k">{t('price_note')}{price ? ` ${money(Math.round(parseFloat(price.replace(',', '.')) * 100) || 0, currency)}` : ''}</div>
        </div>

        <div className="card">
          <div className="lbl">{t('wait')}</div>
          <div className="pills">
            {([15, 30, 60] as const).map((m) => (
              <button key={m} type="button" className={wait === m ? 'on' : ''} onClick={() => setWait(m)}>
                {m === 60 ? `1 ${t('hour')}` : `${m} ${t('min')}`}
              </button>
            ))}
          </div>
          <div className="k">{t('wait_note')}</div>
        </div>
      </div>
      {msg && <div className="toast" role="status">{msg}</div>}
      <button className="main" type="button" disabled={!chosen || busy || form.offer_pending} onClick={send}>
        {t('send_to')}{chosen ? ` · ${chosen.name}` : ''}
      </button>
    </div>
  );
}
